/** @jsxImportSource @emotion/react */
import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import LocationSearch from './LocationSearch';
import { Site } from '../types/Site';

interface BottomNavProps {
  onSearch: (location: { 
    lat: number; 
    lon: number; 
    zoom?: number;
    showAllSites?: boolean;
    selectedSiteId?: string;
  }) => void;
  onLogout: () => void;
  onLayerChange: (layer: string) => void;
  currentLayer: string;
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
  onFilterClick?: () => void;
  userEmail?: string;
  sites: Site[];
  onSiteSelect?: (site: Site) => void;
  onAccountSettings?: () => void;
  onAdminPanel?: () => void;
  onChangeAssetType?: () => void;
  isAdmin?: boolean;
  showRailwayOverlay?: boolean;
  onToggleRailwayOverlay?: () => void;
  onLocateMe?: () => void;
  locating?: boolean;
  onAddFibreJoint?: () => void;
}

interface NavContainerProps {
  isVisible: boolean;
}

const NavContainer = styled.div<NavContainerProps>`
  position: fixed;
  bottom: ${props => props.isVisible ? '0' : '-100%'};
  left: 0;
  right: 0;
  background: rgba(17, 24, 39, 0.95);
  padding: 12px;
  box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 1001;
  backdrop-filter: blur(16px);
  border-top: 1px solid rgba(74, 222, 128, 0.3);
  transition: bottom 0.3s ease-in-out;
  
  /* Ensure visibility on iOS */
  @supports (padding-bottom: env(safe-area-inset-bottom)) {
    padding-bottom: calc(12px + env(safe-area-inset-bottom));
  }

  /* Better mobile layout */
  @media (max-width: 640px) {
    padding: 10px;
    gap: 10px;
    height: auto;
    min-height: 60px;
    flex-wrap: nowrap;
    justify-content: space-between;
  }
`;

const ToggleButton = styled.button`
  position: fixed;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.9);
  width: 24px;
  height: 24px;
  cursor: pointer;
  z-index: 1002;
  transition: all 0.2s ease;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: rgba(74, 222, 128, 0.9);
  }
  
  &:focus {
    outline: none;
  }
  
  @media (max-width: 640px) {
    top: 12px;
    right: 12px;
    width: 22px;
    height: 22px;
  }
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const UserSection = styled.div`
  position: relative;
  
  @media (max-width: 640px) {
    order: 3; /* Move to the right */
  }
`;

const AccountIcon = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(74, 222, 128, 0.2);
  border: 1px solid rgba(74, 222, 128, 0.3);
  color: rgba(255, 255, 255, 0.9);
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  
  &:hover {
    background: rgba(74, 222, 128, 0.3);
  }
  
  @media (max-width: 640px) {
    width: 44px;  /* Larger for touch targets */
    height: 44px;
    font-size: 16px;
  }
`;

const UserDropdown = styled.div<{ show: boolean }>`
  position: absolute;
  bottom: 100%;
  right: 0;
  margin-bottom: 8px;
  background: rgba(26, 26, 26, 0.95);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
  min-width: 200px;
  display: ${props => props.show ? 'block' : 'none'};
  z-index: 1002;
  backdrop-filter: blur(16px);
`;

const DropdownItem = styled.div`
  padding: 12px 16px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  
  &:last-child {
    border-bottom: none;
  }
`;

const DropdownEmail = styled.div`
  padding: 12px 16px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;
  
  strong {
    display: block;
    color: rgba(255, 255, 255, 0.9);
    font-size: 14px;
    margin-bottom: 4px;
  }
`;

const LogoutButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border: none;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(239, 68, 68, 0.2);
  }
`;

const SearchWrapper = styled.div`
  flex: 1;
  min-width: 0; // Prevent flex item from overflowing
  margin: 0 4px;
  
  @media (max-width: 640px) {
    order: 1; /* Move to the left */
    flex: 1 1 auto;
  }
`;

const LayerSelectWrapper = styled.div`
  @media (max-width: 640px) {
    order: 2; /* In the middle */
  }
`;

const LayerSelect = styled.select`
  padding: 8px 32px 8px 12px;
  border-radius: 8px;
  border: 1px solid rgba(74, 222, 128, 0.3);
  font-size: 13px;
  background-color: rgba(74, 222, 128, 0.2);
  cursor: pointer;
  color: rgba(255, 255, 255, 0.9);
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='rgba(255, 255, 255, 0.5)' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 8px center;
  background-size: 12px;
  min-width: 120px;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background-color: rgba(74, 222, 128, 0.25);
    border-color: rgba(74, 222, 128, 0.4);
  }

  &:focus {
    outline: none;
    border-color: rgba(74, 222, 128, 0.5);
    background-color: rgba(74, 222, 128, 0.25);
  }

  option {
    background-color: #1a1a1a;
    color: rgba(255, 255, 255, 0.9);
  }

  @media (max-width: 640px) {
    padding: 10px 28px 10px 12px;
    font-size: 14px;
    min-width: 110px;
    height: 44px;
  }
`;

const RailwayToggle = styled.button<{ active: boolean }>`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid ${props => props.active ? 'rgba(74, 222, 128, 0.6)' : 'rgba(74, 222, 128, 0.3)'};
  font-size: 13px;
  background-color: ${props => props.active ? 'rgba(74, 222, 128, 0.4)' : 'rgba(74, 222, 128, 0.1)'};
  cursor: pointer;
  color: rgba(255, 255, 255, 0.9);
  white-space: nowrap;
  -webkit-tap-highlight-color: transparent;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(74, 222, 128, 0.3);
    border-color: rgba(74, 222, 128, 0.5);
  }

  @media (max-width: 640px) {
    padding: 10px 12px;
    font-size: 14px;
    height: 44px;
  }
`;

const LocateButton = styled.button<{ active?: boolean }>`
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid rgba(74, 222, 128, 0.3);
  font-size: 16px;
  background-color: ${props => props.active ? 'rgba(66, 133, 244, 0.4)' : 'rgba(74, 222, 128, 0.1)'};
  cursor: pointer;
  color: rgba(255, 255, 255, 0.9);
  -webkit-tap-highlight-color: transparent;
  transition: all 0.2s ease;
  line-height: 1;

  &:hover {
    background-color: rgba(66, 133, 244, 0.3);
    border-color: rgba(66, 133, 244, 0.5);
  }

  @media (max-width: 640px) {
    padding: 10px;
    height: 44px;
    width: 44px;
  }
`;

const AddJointButton = styled.button`
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid rgba(0, 188, 212, 0.4);
  font-size: 16px;
  background-color: rgba(0, 188, 212, 0.2);
  cursor: pointer;
  color: rgba(255, 255, 255, 0.9);
  -webkit-tap-highlight-color: transparent;
  transition: all 0.2s ease;
  line-height: 1;

  &:hover {
    background-color: rgba(0, 188, 212, 0.35);
    border-color: rgba(0, 188, 212, 0.6);
  }

  /* Hide on touch devices - long-press works there instead */
  @media (pointer: coarse) {
    display: none;
  }

  @media (max-width: 640px) {
    display: none;
  }
`;

const LAYER_OPTIONS = [
  { value: 'standard', label: 'Standard Map' },
  { value: 'satellite', label: 'Satellite' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'terrain', label: 'Terrain' },
  { value: 'dark', label: 'Dark Mode' }
];

const BottomNav = ({
  onSearch,
  onLogout,
  onLayerChange,
  currentLayer,
  hasActiveFilters,
  onClearFilters,
  onFilterClick,
  userEmail,
  sites,
  onSiteSelect,
  onAccountSettings,
  onAdminPanel,
  onChangeAssetType,
  isAdmin,
  showRailwayOverlay,
  onToggleRailwayOverlay,
  onLocateMe,
  locating,
  onAddFibreJoint
}: BottomNavProps): JSX.Element => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [isNavVisible, setIsNavVisible] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLButtonElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        iconRef.current && 
        !iconRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleNavVisibility = () => {
    setIsNavVisible(!isNavVisible);
  };

  // Get first letter of email for icon
  const userInitial = userEmail ? userEmail[0].toUpperCase() : '?';

  return (
    <>
      <ToggleButton onClick={toggleNavVisibility} aria-label={isNavVisible ? "Hide navigation" : "Show navigation"}>
        {isNavVisible ? (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="18 15 12 9 6 15" />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        )}
      </ToggleButton>
      <NavContainer isVisible={isNavVisible}>
        <SearchWrapper>
          <LocationSearch 
            onSearch={onSearch}
            sites={sites}
            isLoading={false}
            onFilterClick={onFilterClick}
            onClearFilters={onClearFilters}
            hasActiveFilters={hasActiveFilters}
            onSiteSelect={onSiteSelect}
            onAccountSettings={onAccountSettings}
            onAdminPanel={onAdminPanel}
            isAdmin={isAdmin}
            onChangeAssetType={onChangeAssetType}
          />
        </SearchWrapper>
        
        <LayerSelectWrapper>
          <LayerSelect
            value={currentLayer}
            onChange={(e) => onLayerChange(e.target.value)}
          >
            {LAYER_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </LayerSelect>
        </LayerSelectWrapper>
        {onToggleRailwayOverlay && (
          <RailwayToggle
            active={!!showRailwayOverlay}
            onClick={onToggleRailwayOverlay}
            title="Toggle OpenRailwayMap overlay"
          >
            {showRailwayOverlay ? 'Rail: ON' : 'Rail: OFF'}
          </RailwayToggle>
        )}
        {onLocateMe && (
          <LocateButton
            onClick={onLocateMe}
            title="Zoom to my location"
            active={locating}
          >
            {locating ? '...' : '📍'}
          </LocateButton>
        )}
        {onAddFibreJoint && (
          <AddJointButton
            onClick={onAddFibreJoint}
            title="Add new fibre joint"
          >
            ➕
          </AddJointButton>
        )}
        
        <UserSection>
          <AccountIcon onClick={() => setShowDropdown(!showDropdown)} ref={iconRef}>
            👤
          </AccountIcon>
          <UserDropdown 
            show={showDropdown} 
            ref={dropdownRef}
          >
            {userEmail && (
              <DropdownEmail>
                <strong>Account</strong>
                {userEmail}
              </DropdownEmail>
            )}
            {onAccountSettings && (
              <DropdownItem onClick={() => {
                setShowDropdown(false);
                onAccountSettings();
              }}>
                ⚙️ Settings
              </DropdownItem>
            )}
            {onChangeAssetType && (
              <DropdownItem onClick={() => {
                setShowDropdown(false);
                onChangeAssetType();
              }}>
                🏠 Main Menu
              </DropdownItem>
            )}
            {isAdmin && onAdminPanel && (
              <DropdownItem onClick={() => {
                setShowDropdown(false);
                onAdminPanel();
              }}>
                🔐 Admin Panel
              </DropdownItem>
            )}
            <LogoutButton onClick={onLogout}>
              Sign Out
            </LogoutButton>
          </UserDropdown>
        </UserSection>
      </NavContainer>
    </>
  );
};

export default BottomNav; 